import { KeyRelationship, Note } from "../../../Data/HexMap/Note";
import { Music } from "../../../Util/Music/Music";
import { ChapterConfig } from "../../ChapterConfig";
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox";
import { ChapterNav, ChapterNavItem } from "../ChapterNav/ChapterNav";
import { ChapterRoutes } from "../ChapterNav/ChapterRoutes";
import { ClearHelpers } from "../../Helpers/ClearHelpers/ClearHelpers";
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup";
import { BuildHexMap} from "../../../Data/HexMap/HexMapBuilder";
import { HexMap } from "../../../Data/HexMap/HexMap";
import { TransformTranslate } from "../../../Util/Transform/Transform";
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier";
import { All } from "../../../Util/Constants/Quantities";
import { MutableRefObject, useRef, useState } from "react";
import { produce } from "immer";
import './HexMapChapter.scss'
import { Key } from "../../../Data/HexMap/Key";
import { DotNav } from "../../../Comps/DotNav/DotNav";
import { Helpers } from "../../Helpers/Helpers";
import { HexMapBuilder } from "../../Exercises/HexMapBuilder/HexMapBuilder";
import { NotePlayer } from "../../../Sound/NotePlayer";
import { useNotePlayer } from "../../../Sound/SoundStore";
export function HexMapChapter(props:ChapterConfig){
    const lessonElement:JSX.Element =   <ClearHelpers openHelpers={[Helpers.HexPattern]}>
                                            <Lesson/>
                                        </ClearHelpers>
    const builderExerciseElement:JSX.Element =   <ClearHelpers openHelpers={[Helpers.HexPattern]}>
                                            <HexMapBuilder/>
                                        </ClearHelpers>
    const subPaths:ChapterNavItem[] = [
        {title:'Lesson', path:'lesson', element:lessonElement},
        {title:'Builder Exercise', path:'builder', element:builderExerciseElement}
    ]
    return  <div className={'hex-map-chapter'}>
                <h1>The Hex Map Structure</h1>
                <ChapterNav path={props.path} subPaths={subPaths}/>
                <ChapterRoutes chapterNavs={subPaths}/>
            </div>
}

export function Lesson(){
    const radius:number = 62
    const columns:number = 13
    const rows:number = 9
    const width:number = 800
    const height:number = 600
    const tonic:number = Music.E
    const centerKey:number = 58
    const notePlayer:MutableRefObject<NotePlayer> = useRef(useNotePlayer())
    const getMap = () => {  
        const mapData:HexMap = BuildHexMap({columns:columns, rows:rows, tonic:tonic, radius:radius})
        const mod:HexMapModifier = new HexMapModifier(mapData)
        const centerNotes:number[] = mapData.keys.flat()[centerKey].noteIndexes
        mod.notes_setDefined([All], false)
        mod.notes_setVisible([All], false)
        mod.notes_setVisible(centerNotes, true)
        mod.notes_addClasses([All], ['disabled'])
        mod.notes_removeClasses(centerNotes, ['disabled'])
        mod.keys_addClasses([All], ['disabled'])
        mod.keys_removeClasses([centerKey], ['disabled'])
        mod.notes_setDefined(centerNotes, true)
        return mapData
    }
    const [hexMap, setHexMap] = useState(getMap())
    const [scale, setScale] = useState(1.5)
    const [navIndex, setNavIndex] = useState(0)
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(hexMap))
    const getFilledKeys = (note:Note, hexMap:HexMap) => {
        const filledKeys:number[] = []
        const keyRels:KeyRelationship[] = note.keys
        keyRels.forEach((keyRel:KeyRelationship)=>{
            const keyIndex:number = keyRel.keyIndex
            const key:Key = hexMap.keys.flat()[keyIndex]
            const noteIndexes:number[] = key.noteIndexes
            const definedNotes:number[] = noteIndexes.filter((noteIndex:number)=>{
                return(hexMap.notes[noteIndex].defined)
            })
            if(definedNotes.length === 7){
                filledKeys.push(keyIndex)
            }
        })
        return filledKeys
    }
    const onNoteClick = (note:Note) => {
        const noteIndexes:number[] = [note.params.noteIndex]
        notePlayer.current.playNote(note.params.tonic, true)
        setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.notes_setDefined(noteIndexes, true)
                //mod.current.notes_setSelected(noteIndexes)
                mod.current.notes_removeClasses(noteIndexes, ['disabled'])
                const keys:number[] = getFilledKeys(note, draft)
                mod.current.keys_removeClasses(keys, ['disabled'])
                mod.current.setDraft(draft)
        }))
        return        
    }
    const showHideKey = (key:Key) => {
        const keyIndex:number = key.params.keyIndex
        setHexMap(produce((draft:HexMap)=>{
            mod.current.setDraft(draft)
            const key:Key = draft.keys.flat()[keyIndex]
            if(key.classNames.includes('disabled')) {
                //mod.current.keys_removeClasses([key.params.keyIndex], ['disabled'])
                mod.current.keys_removeClasses([keyIndex], ['disabled'])
                mod.current.notes_setVisible(key.noteIndexes, true)
            }
            else{
                mod.current.keys_addClasses([keyIndex], ['disabled'])
            }
            mod.current.setDraft(draft)
        }))
    }
    const onKeyClick = (key:Key) => {
        if(navIndex === 3 || navIndex === 0){
            showHideKey(key)
            return
        }
        const keyNotes:number[] = key.noteIndexes
        if(navIndex === 2){
            setHexMap(produce((draft:HexMap)=>{ 
                    mod.current.setDraft(draft)
                    mod.current.keys_toggleClasses([key.params.keyIndex],['disabled'])
                    mod.current.keys_setDefined([key.params.keyIndex],!key.defined)
                    mod.current.setDraft(draft)
            }))
            return
        }
        setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_toggleClasses([key.params.keyIndex],['disabled'])
                mod.current.notes_setDefined(keyNotes, true)
                mod.current.notes_removeClasses(keyNotes,['disabled'])
                mod.current.setDraft(draft)
        }))
        return         
    }
    const showTwelveKeys = () => {
        const keyIndexes:number[] = [43,44,45,46,57,58,59,60,70,71,72,73]
        let noteIndexes:number[] = []
        keyIndexes.forEach((keyIndex:number)=>{
            noteIndexes = noteIndexes.concat(hexMap.keys.flat()[keyIndex].noteIndexes)
        })
         setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_setOpen([All], true)
                mod.current.notes_setDefined([All], false)
                mod.current.notes_setDefined(noteIndexes, true)
                mod.current.notes_removeClasses(noteIndexes, ['disabled'])
                mod.current.keys_addClasses([All], ['disabled'])
                mod.current.keys_removeClasses(keyIndexes, ['disabled'])
                mod.current.setDraft(draft)
        }))
        return        
    }
    const closeTwelveKeys = () => {
        const keyIndexes:number[] = [43,44,45,46,57,58,59,60,70,71,72,73]
         setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_setOpen([All], false)
                mod.current.keys_addClasses([All], ['disabled'])
                mod.current.keys_removeClasses(keyIndexes, ['disabled'])
                mod.current.keys_setDefined([All], false)
                mod.current.keys_setDefined(keyIndexes, true)
                mod.current.setDraft(draft)
        }))
        return        
    }
    const expandFromTwelveKeys = () => {
        const keyIndexes:number[] = [43,44,45,46,57,58,59,60,70,71,72,73]
         setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_setOpen([All], false)
                mod.current.keys_addClasses([All], ['disabled'])
                mod.current.keys_removeClasses(keyIndexes, ['disabled'])
                mod.current.keys_setDefined([All], true)
                mod.current.setDraft(draft)
        }))
        return        
    }    
    const showTwelvePattern = () => {
        const keyIndexes:number[] = [43,44,45,46,57,58,59,60,70,71,72,73]
        const otherPatternIndexes:number[] = [0,1,2,13,14,15,26,27,28,29,52,65,51,80,81,82,83,93,94,95,96,107,108,109,110, 7,8,9,10,20,21,22,23,34,35,36,37,88,89,90,101,102,103,115,116]
        const allKeysIndexes:number[] = keyIndexes.concat(otherPatternIndexes)
         setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_setOpen([All], false)
                mod.current.keys_addClasses([All], ['disabled'])
                mod.current.keys_removeClasses(allKeysIndexes, ['disabled'])
                mod.current.keys_setDefined([All], true)
                mod.current.setDraft(draft)
        }))
    }    
    const showSideBySide = () => {
        const keyIndexes:number[] = [58,59]
        const otherIndexes:number[] = [43,44,45,46,57,60,70,71,72,73]
        setHexMap(produce((draft:HexMap)=>{ 
                mod.current.setDraft(draft)
                mod.current.keys_setOpen([All], false)
                mod.current.keys_addClasses([All], ['disabled'])
                mod.current.keys_removeClasses(keyIndexes.concat(otherIndexes), ['disabled'])
                mod.current.keys_setDefined([All], false)
                mod.current.keys_setOpen(keyIndexes, true)
                mod.current.notes_setDefault([All])
                mod.current.notes_removeClasses([All], ['disabled'])
                mod.current.setDraft(draft)
        }))
    }
    const onNavClick = (navIndex:number) => {
        setNavIndex(navIndex)
        if(navIndex === 0){
            setScale(1.5)
            setHexMap(getMap())
            return
        }
        if(navIndex === 1){
            setScale(1)
            showTwelveKeys()
        }
        if(navIndex === 2){
            setScale(1)
            closeTwelveKeys()
        }
        if(navIndex === 3){
            setScale(0.5)
            expandFromTwelveKeys()
        }
        if(navIndex === 4){
            setScale(0.5)
            showTwelvePattern()
        }
        if(navIndex === 5){
            setScale(1)
            closeTwelveKeys()
        }
        if(navIndex === 6){
            setScale(1)
            showSideBySide()
        }
    }
    return(
        <OutlineBox title={'The HexMap Structure'} width={width}>
            <div className={'Lesson'}>
                <svg width={width} height={height}>
                    <g transform={TransformTranslate(width/2, height/2)}>
                        <g className={'scale'} style={{scale:scale}}>
                        <HxMapGroup onNoteClick={onNoteClick}
                                    onKeyClick={onKeyClick} 
                                    hexMap={hexMap}/>
                        </g>
                    </g>
                </svg>
            </div>
            <DotNav length={7} selectedIndex={navIndex} onClick={onNavClick}/>
 
        </OutlineBox>
    )
}
